/**
 * The client portal map's own parts (Diurnal parity 03-053, 05-025; docs/specs/portal/client-area.md §11):
 *
 * - `usePortalMap`: the project's aircraft and vehicles (asked again every minute, as the cloud poller stores new
 *   fixes) and its published hazards, read as the client reads them; Preview as client reads the same.
 * - `PortalMapControls`: what the map shows (aircraft and vehicles, the trail, hazards, landholder properties).
 * - `PortalTrackingList`: each aircraft and vehicle under the map, with take-off, flight time and landing (decision
 *   64), and what the client can see in plain words. Never SOS or lost tracking.
 * - `ClientMapSettings` (staff): "Publish hazards to the client portal" and "Show the landholder boundaries to the
 *   client" for the project (decisions 82, 83; every staff role, decision 625), the hazard pack the hazards come
 *   from, and the aircraft and vehicles Teslyn Field shows to the project's client.
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  PORTAL_TRACKING_REFRESH_MS,
  PORTAL_TRAIL_DEFAULT,
  PORTAL_TRAIL_HOURS,
  canPortal,
  portalAssetName,
  portalDate,
  portalEntitlementWords,
  portalHazardCounts,
  portalTracking,
  portalWindowWords,
  type PortalAssetRow,
  type PortalHazardsAnswer,
  type PortalTracking,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { CheckField, SelectField } from '../components/Fields.tsx';
import type { PortalProject } from './api.ts';
import {
  loadPortalHazards,
  loadPortalMapSetting,
  loadPortalTracking,
  loadProjectAssignments,
  setPortalMap,
  showCurrentPackToClient,
  type PortalMapSetting,
  type ProjectPack,
} from './portalMapApi.ts';

/** The viewer's UTC offset in hours (the times on the client's map are the viewer's own). */
export const viewerOffsetH = (): number => -new Date().getTimezoneOffset() / 60;

export interface PortalMapData {
  readonly tracking: PortalTracking | null;
  /** The hours of positions `tracking` holds (the trail asked, or the replay's 48), or null before the first answer. */
  readonly trackingHours: number | null;
  readonly assets: readonly PortalAssetRow[];
  readonly trackingError: string | null;
  readonly hazards: PortalHazardsAnswer | null;
  readonly hazardsError: string | null;
}

/** The client portal map's aircraft, vehicles and hazards (nothing is asked while `enabled` is false). */
export function usePortalMap(
  sb: SupabaseClient,
  projectId: string,
  enabled: boolean,
  hours: number,
): PortalMapData {
  const [tracking, setTracking] = useState<PortalTracking | null>(null);
  const [trackingHours, setTrackingHours] = useState<number | null>(null);
  const [assets, setAssets] = useState<readonly PortalAssetRow[]>([]);
  const [trackingError, setTrackingError] = useState<string | null>(null);
  const [hazards, setHazards] = useState<PortalHazardsAnswer | null>(null);
  const [hazardsError, setHazardsError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    const load = (): void => {
      loadPortalTracking(sb, projectId, hours)
        .then((r) => {
          if (!live) return;
          setAssets(r.assets);
          setTracking(portalTracking({ ...r, nowMs: Date.now(), offsetH: viewerOffsetH() }));
          setTrackingHours(hours);
          setTrackingError(null);
        })
        .catch((e: unknown) => live && setTrackingError(e instanceof Error ? e.message : String(e)));
    };
    load();
    const timer = setInterval(load, PORTAL_TRACKING_REFRESH_MS);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [sb, projectId, enabled, hours]);

  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    loadPortalHazards(sb, projectId)
      .then((h) => live && setHazards(h))
      .catch((e: unknown) => live && setHazardsError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [sb, projectId, enabled]);

  return { tracking, trackingHours, assets, trackingError, hazards, hazardsError };
}

export interface PortalMapShown {
  readonly aircraft: boolean;
  readonly hours: number;
  readonly hazards: boolean;
  readonly landholders: boolean;
}

export const PORTAL_MAP_SHOWN: PortalMapShown = {
  aircraft: true,
  hours: PORTAL_TRAIL_DEFAULT,
  hazards: true,
  landholders: true,
};

/** What the client's map shows: phone first, the ticks wrap under each other. */
export function PortalMapControls({
  data,
  shown,
  onChange,
}: {
  readonly data: PortalMapData;
  readonly shown: PortalMapShown;
  readonly onChange: (s: PortalMapShown) => void;
}): ReactElement {
  const counts = portalHazardCounts(data.hazards?.layers ?? []);
  const hours: ReadonlyArray<readonly [string, string]> = PORTAL_TRAIL_HOURS.map((h) => [
    String(h),
    `${h} h`,
  ]);
  return (
    <fieldset className="portal-layers" aria-label="What the map shows">
      <CheckField
        label={`Aircraft and vehicles (${data.tracking?.aircraft.length ?? 0})`}
        checked={shown.aircraft}
        onChange={(v) => onChange({ ...shown, aircraft: v })}
      />
      <SelectField<string>
        label="Trail"
        value={String(shown.hours)}
        onChange={(v) => onChange({ ...shown, hours: Number(v) })}
        options={hours}
        disabled={!shown.aircraft}
      />
      {counts.hazards > 0 && (
        <CheckField
          label={`Hazards (${counts.hazards})`}
          checked={shown.hazards}
          onChange={(v) => onChange({ ...shown, hazards: v })}
        />
      )}
      {counts.landholders > 0 && (
        <CheckField
          label="Landholder properties"
          checked={shown.landholders}
          onChange={(v) => onChange({ ...shown, landholders: v })}
        />
      )}
    </fieldset>
  );
}

/** The aircraft and vehicles under the client's map, and what the client can see in words. */
export function PortalTrackingList({
  data,
  projectNumber,
  hours,
}: {
  readonly data: PortalMapData;
  readonly projectNumber: string;
  readonly hours: number;
}): ReactElement {
  const offsetH = viewerOffsetH();
  const rows = data.tracking?.rows ?? [];
  const seen = new Set(rows.map((r) => r.id));
  const quiet = [...new Map(data.assets.map((a) => [`asset:${a.asset_id}`, a])).entries()]
    .filter(([id]) => !seen.has(id))
    .map(([, a]) => portalAssetName(a));
  const words = portalEntitlementWords(data.assets, projectNumber, offsetH);
  const pack = data.hazards?.state === 'published' ? data.hazards.pack : null;
  return (
    <section className="stack" aria-label="Aircraft and vehicles">
      <h2 className="heading">Aircraft and vehicles</h2>
      {data.trackingError && (
        <p className="caption muted" role="status">
          The aircraft could not be loaded: {data.trackingError}
        </p>
      )}
      {!data.trackingError && data.assets.length === 0 && (
        <p className="body">No aircraft or vehicle is shown on this project at the moment.</p>
      )}
      {rows.length > 0 && (
        <ul className="portal-tracking">
          {rows.map((r) => (
            <li key={r.id} className="portal-tracking__row">
              <span className="portal-tracking__name">{r.label}</span>
              <span className="portal-tracking__line body">
                {[r.where, r.figures].filter(Boolean).join(' · ')}
              </span>
              {r.flight && (
                <span className="portal-tracking__line data" aria-label={r.flightWords}>
                  {r.flight}
                </span>
              )}
              <span className="portal-tracking__line caption muted">{r.lastFix}</span>
            </li>
          ))}
        </ul>
      )}
      {quiet.length > 0 && (
        <p className="caption muted">
          No position in the last {hours} h: {quiet.join(', ')}.
        </p>
      )}
      {words.length > 0 && (
        <ul className="stack" aria-label="What you can see">
          {words.map((w, i) => (
            <li key={i} className="caption muted">
              {w}
            </li>
          ))}
        </ul>
      )}
      {data.hazardsError && (
        <p className="caption muted" role="status">
          The hazards could not be loaded: {data.hazardsError}
        </p>
      )}
      {pack && (
        <p className="caption muted">
          Hazards as published on {portalDate(pack.published_at, offsetH)}. They are shown for information and
          may not include every hazard.
        </p>
      )}
    </section>
  );
}

/** Staff: what the project's client portal map shows (every staff role chooses; archived projects read only). */
export function ClientMapSettings({
  sb,
  project,
  roles,
}: {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
  readonly roles: readonly string[];
}): ReactElement {
  const [setting, setSetting] = useState<PortalMapSetting | null>(null);
  const [pack, setPack] = useState<ProjectPack | null>(null);
  const [assets, setAssets] = useState<readonly PortalAssetRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    let live = true;
    setError(null);
    Promise.all([loadPortalMapSetting(sb, project.id), loadProjectAssignments(sb, project.id)])
      .then(([s, a]) => {
        if (!live) return;
        setSetting(s.setting);
        setPack(s.pack);
        setAssets(a);
      })
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [sb, project.id]);
  useEffect(load, [load]);
  const may = canPortal(roles, 'portal.map') && project.status !== 'archived';
  const offsetH = viewerOffsetH();
  const change = async (hazards: boolean, landholders: boolean): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      setSetting(await setPortalMap(sb, project.id, hazards, landholders));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const showCurrent = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await showCurrentPackToClient(sb, project.id);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const newer = setting?.hazardsPublished && pack && pack.clientVersion !== pack.version;
  const shownTo = useMemo(
    () =>
      (assets ?? []).map((a) => {
        const w = portalWindowWords(a, offsetH);
        return `${portalAssetName(a)}${w ? ` ${w}` : ''}`;
      }),
    [assets, offsetH],
  );
  return (
    <section className="panel stack" aria-label="Client portal map">
      <h2 className="heading">Client portal map</h2>
      <p className="body">
        The client sees the lines remaining, the photos shared with them, the aircraft and vehicles shown to
        this project&rsquo;s client while they are assigned, and the hazards when you publish them here. Never
        SOS, lost tracking, public air traffic, landholder contacts, notes or flags.
      </p>
      {error && (
        <p className="field__error caption" role="alert">
          {error}
        </p>
      )}
      {setting && (
        <>
          <CheckField
            label="Publish hazards to the client portal"
            checked={setting.hazardsPublished}
            disabled={!may || busy}
            onChange={(v) => void change(v, v && setting.landholdersShared)}
          />
          <CheckField
            label="Show the landholder boundaries to the client"
            checked={setting.landholdersShared}
            disabled={!may || busy || !setting.hazardsPublished}
            onChange={(v) => void change(setting.hazardsPublished, v)}
          />
          <p className="caption muted">
            {pack
              ? `The hazards are those of the hazard pack "${pack.name}" (version ${pack.version}, synced ${portalDate(pack.publishedAt, offsetH)}${pack.publishedByName ? ` by ${pack.publishedByName}` : ''}): its hazard maps, pins and hazard sites, without its survey lines and block boundaries (the client’s map draws its own). ${pack.properties > 0 ? `It holds ${pack.properties} landholder ${pack.properties === 1 ? 'property' : 'properties'}, shown by name and outline only when you show the landholder boundaries. ` : ''}The client sees the version you show them, not each new one.`
              : 'No hazard pack in Teslyn Cloud yet, so the client’s map shows no hazards. It comes up from Teslyn Field by itself when a hazard map is made.'}
          </p>
          {newer && (
            <div className="row row--wrap">
              <p className="caption">
                The client has {pack.clientVersion === null ? 'no copy yet' : `version ${pack.clientVersion}`}
                .
              </p>
              {may && (
                <Button variant="secondary" busy={busy} onClick={() => void showCurrent()}>
                  Show version {pack.version} to the client
                </Button>
              )}
            </div>
          )}
          {setting.updatedAt && (
            <p className="caption muted">
              Last changed {portalDate(setting.updatedAt, offsetH)}
              {setting.updatedByName ? ` by ${setting.updatedByName}` : ''}.
            </p>
          )}
          {!may && project.status === 'archived' && (
            <p className="caption muted">An archived project is read only.</p>
          )}
        </>
      )}
      {assets && (
        <p className="caption muted">
          {shownTo.length > 0
            ? `Aircraft and vehicles shown to the client: ${shownTo.join('; ')}.`
            : 'No aircraft or vehicle is shown to this project’s client. In Teslyn Field, Settings, "Tracker aircraft and vehicles", tick "Show to this project’s client" (tenant admins and managers).'}
        </p>
      )}
      <p className="caption muted">Preview as client shows the client&rsquo;s map exactly.</p>
    </section>
  );
}
